import { NgModule } from '@angular/core';
import {
  NavigationCancel,
  NavigationError,
  Router,
  RouterStateSnapshot,
  RoutesRecognized,
} from '@angular/router';
import { Store } from '@ngrx/store';
import { of } from 'rxjs/observable/of';
import {
  DefaultRouterStateSerializer,
  RouterStateSerializer,
} from './serializer';
/**
 * An action dispatched when the router navigates.
 */
export const ROUTER_NAVIGATION = 'ROUTER_NAVIGATION';

/**
 * Payload of ROUTER_NAVIGATION.
 */
export type RouterNavigationPayload<T> = {
  routerState: T;
  event: RoutesRecognized;
};

/**
 * An action dispatched when the router navigates.
 */
export type RouterNavigationAction<T = RouterStateSnapshot> = {
  type: typeof ROUTER_NAVIGATION;
  payload: RouterNavigationPayload<T>;
};
/**
 * An action dispatched when the router cancels navigation.
 */
export const ROUTER_CANCEL = 'ROUTER_CANCEL';

/**
 * Payload of ROUTER_CANCEL.
 */
export type RouterCancelPayload<T, V> = {
  routerState: V;
  storeState: T;
  event: NavigationCancel;
};

/**
 * An action dispatched when the router cancel navigation.
 */
export type RouterCancelAction<T, V = RouterStateSnapshot> = {
  type: typeof ROUTER_CANCEL;
  payload: RouterCancelPayload<T, V>;
};
/**
 * An action dispatched when the router errors.
 */
export const ROUTER_ERROR = 'ROUTE_ERROR';

/**
 * Payload of ROUTER_ERROR.
 */
export type RouterErrorPayload<T, V> = {
  routerState: V;
  storeState: T;
  event: NavigationError;
};

/**
 * An action dispatched when the router errors.
 */
export type RouterErrorAction<T, V = RouterStateSnapshot> = {
  type: typeof ROUTER_ERROR;
  payload: RouterErrorPayload<T, V>;
};

/**
 * An union type of router actions.
 */
export type RouterAction<T, V = RouterStateSnapshot> =
  | RouterNavigationAction<T>
  | RouterCancelAction<T, V>
  | RouterErrorAction<T, V>;

export type RouterReducerState<T = RouterStateSnapshot> = {
  state: T;
  navigationId: number;
};
/**
 * @template T
 * @param {?} state
 * @param {?} action
 * @return {?}
 */
export function routerReducer<T = RouterStateSnapshot>(
  state: RouterReducerState<T>,
  action: RouterAction<any>
): RouterReducerState<T> {
  switch (action.type) {
    case ROUTER_NAVIGATION:
    case ROUTER_ERROR:
    case ROUTER_CANCEL:
      return {
        state: action.payload.routerState,
        navigationId: action.payload.event.id,
      };
    default:
      return state;
  }
}
/**
 * Connects RouterModule with StoreModule.
 * 
 * During the navigation, before any guards or resolvers run, the router will dispatch
 * a ROUTER_NAVIGATION action, which has the following signature:
 * 
 * ```
 * export type RouterNavigationPayload = {
 *   routerState: RouterStateSnapshot,
 *   event: RoutesRecognized
 * }
 * ```
 * 
 * Either a reducer or an effect can be invoked in response to this action.
 * If the invoked reducer throws, the navigation will be canceled.
 * 
 * If navigation gets canceled because of a guard, a ROUTER_CANCEL action will be
 * dispatched. If navigation results in an error, a ROUTER_ERROR action will be dispatched.
 * 
 * Both ROUTER_CANCEL and ROUTER_ERROR contain the store state before the navigation
 * which can be used to restore the consistency of the store.
 * 
 * Usage:
 * 
 * ```typescript
 * \@NgModule({ 
 *   declarations: [AppCmp, SimpleCmp],
 *   imports: [
 *     BrowserModule,
 *     StoreModule.forRoot(mapOfReducers),
 *     RouterModule.forRoot([
 *       { path: '', component: SimpleCmp },
 *       { path: 'next', component: SimpleCmp }
 *     ]),
 *     StoreRouterConnectingModule
 *   ],
 *   bootstrap: [AppCmp]
 * })
 * export class AppModule {
 * }
 * ```
 */
export class StoreRouterConnectingModule {
private routerState: RouterStateSnapshot;
private storeState: any;
private lastRoutesRecognized: RoutesRecognized;
private dispatchTriggeredByRouter: boolean = false;
private navigationTriggeredByDispatch: boolean = false;
/**
 * @param {?} store
 * @param {?} router
 * @param {?} serializer
 */
constructor(
private store: Store<any>,
private router: Router,
private serializer: RouterStateSerializer<RouterStateSnapshot>
  ) {
    this.setUpBeforePreactivationHook();
    this.setUpStoreStateListener();
    this.setUpStateRollbackEvents();
  }
/**
 * @return {?}
 */
private setUpBeforePreactivationHook(): void {
    ( /** @type {?} */((<any>this.router))).hooks.beforePreactivation = (
      routerState: RouterStateSnapshot
    ) => {
      this.routerState = this.serializer.serialize(routerState);
      if (this.shouldDispatchRouterNavigation())
        this.dispatchRouterNavigation();
      return of(true);
    };
  }
/**
 * @return {?}
 */
private setUpStoreStateListener(): void {
    this.store.subscribe(s => {
      this.storeState = s;
    });
    this.store.select('routerReducer').subscribe(() => {
      this.navigateIfNeeded();
    });
  }
/**
 * @return {?}
 */
private shouldDispatchRouterNavigation(): boolean {
    if (!this.storeState['routerReducer']) return true;
    return !this.navigationTriggeredByDispatch;
  }
/**
 * @return {?}
 */
private navigateIfNeeded(): void {
    if (
      !this.storeState['routerReducer'] ||
      !this.storeState['routerReducer'].state
    ) {
      return;
    }
    if (this.dispatchTriggeredByRouter) return;

    if (this.router.url !== this.storeState['routerReducer'].state.url) {
      this.navigationTriggeredByDispatch = true;
      this.router.navigateByUrl(this.storeState['routerReducer'].state.url);
    }
  }
/**
 * @return {?}
 */
private setUpStateRollbackEvents(): void {
    this.router.events.subscribe(e => {
      if (e instanceof RoutesRecognized) {
        this.lastRoutesRecognized = e;
      } else if (e instanceof NavigationCancel) {
        this.dispatchRouterCancel(e);
      } else if (e instanceof NavigationError) {
        this.dispatchRouterError(e);
      }
    });
  }
/**
 * @return {?}
 */
private dispatchRouterNavigation(): void {
    this.dispatchRouterAction(ROUTER_NAVIGATION, {
      routerState: this.routerState,
      event: new RoutesRecognized(
        this.lastRoutesRecognized.id,
        this.lastRoutesRecognized.url,
        this.lastRoutesRecognized.urlAfterRedirects,
        this.routerState
      ),
    });
  }
/**
 * @param {?} event
 * @return {?}
 */
private dispatchRouterCancel(event: NavigationCancel): void {
    this.dispatchRouterAction(ROUTER_CANCEL, {
      routerState: this.routerState,
      storeState: this.storeState,
      event,
    });
  }
/**
 * @param {?} event
 * @return {?}
 */
private dispatchRouterError(event: NavigationError): void {
    this.dispatchRouterAction(ROUTER_ERROR, {
      routerState: this.routerState,
      storeState: this.storeState,
      event: new NavigationError(event.id, event.url, `${event}`),
    });
  }
/**
 * @param {?} type
 * @param {?} payload
 * @return {?}
 */
private dispatchRouterAction(type: string, payload: any): void {
    this.dispatchTriggeredByRouter = true;
    try {
      this.store.dispatch({ type, payload });
    } finally {
      this.dispatchTriggeredByRouter = false;
      this.navigationTriggeredByDispatch = false;
    }
  }
static decorators: DecoratorInvocation[] = [
{ type: NgModule, args: [{
  providers: [
    { provide: RouterStateSerializer, useClass: DefaultRouterStateSerializer },
  ],
}, ] },
];
/**
 * @nocollapse
 */
static ctorParameters: () => ({type: any, decorators?: DecoratorInvocation[]}|null)[] = () => [
{type: Store, },
{type: Router, },
{type: RouterStateSerializer, },
];
}

function StoreRouterConnectingModule_tsickle_Closure_declarations() {
/** @type {?} */
StoreRouterConnectingModule.decorators;
/**
 * @nocollapse
 * @type {?}
 */
StoreRouterConnectingModule.ctorParameters;
/** @type {?} */
StoreRouterConnectingModule.prototype.routerState;
/** @type {?} */
StoreRouterConnectingModule.prototype.storeState;
/** @type {?} */
StoreRouterConnectingModule.prototype.lastRoutesRecognized;
/** @type {?} */
StoreRouterConnectingModule.prototype.dispatchTriggeredByRouter;
/** @type {?} */
StoreRouterConnectingModule.prototype.navigationTriggeredByDispatch;
/** @type {?} */
StoreRouterConnectingModule.prototype.store;
/** @type {?} */
StoreRouterConnectingModule.prototype.router;
/** @type {?} */
StoreRouterConnectingModule.prototype.serializer;
}


interface DecoratorInvocation {
  type: Function;
  args?: any[];
}



// WEBPACK FOOTER //
// ../../../modules/router-store/src/router_store_module.ts